import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { MY_FOODS_PATH } from '@/constants/paths';
import { FoodRankCard } from '@design-system/Cards/FoodRankCard';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { getMyFoodsPageUrl } from '@/utils/nutrition/getMyFoodsPageUrl';
import type { FoodRank } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { NutritionPageServingGuide } from '@screens/NutritionPage/NutritionPageServingGuide';
import { sortData } from '@utils/sortData';
import { NutritionPageCommonSkeleton } from './NutritionPageCommonSkeleton';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';

export interface NutritionPageFoodsSectionProps {
  isLoading?: boolean;
  foodRanks?: FoodRank[];
  isError?: boolean;
  'data-testid'?: string;
}
const handleFoodRedirection = (rankId: number, title: string) => {
  EventTracker.trackEvent(
    'nutrition',
    `nutrition_myFoods_gateway_${title.toLocaleLowerCase()}`,
  );
  return getMyFoodsPageUrl({ rank: rankId });
};

const handleAllFoodsRedirection = () => {
  EventTracker.trackEvent('nutrition', 'nutrition_myFoods_gateway_all_foods');
  return MY_FOODS_PATH;
};

const renderFoodRanks = (foodRanks?: FoodRank[]) => {
  if (!foodRanks?.length) return null;

  const sortedFoodRanks = sortData(foodRanks);

  const renderedFoodRanks = sortedFoodRanks.map((rank) => (
    <Link
      key={rank.id}
      variant="flat"
      to={handleFoodRedirection(rank.id, rank.title)}
      className="lg:flex-none lg:w-[calc((100%-24px)/4)] first:lg:ml-0 last:lg:mr-0"
      data-testid={`nutrition-foods-card-${rank.title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="py-1">
        <FoodRankCard
          title={rank.title}
          color={rank.color}
          foodImages={rank.image_url}
          description={rank.description}
        />
      </div>
    </Link>
  ));

  return renderedFoodRanks;
};

export const NutritionPageFoodsSection = ({
  isLoading,
  foodRanks,
  isError,
  'data-testid': testId,
}: NutritionPageFoodsSectionProps) => {
  if (isLoading) {
    return <NutritionPageCommonSkeleton />;
  }

  if (isError || !foodRanks) {
    return;
  }
  return (
    <>
      <h2 className="mb-4 typography-h1 text-primary z-0" data-testid={testId}>My Foods</h2>

      <p className="mb-6 typography-body text-tertiary max-w-xl z-0" data-testid="nutrition-foods-description">
        Build a strong foundation for your health with your personalized food
        list and learn how every bite influences your health.
      </p>

      <div className="relative" data-testid="nutrition-foods-grid">
        <div
          className={cn(
            'grid grid-cols-2 gap-2',
            'lg:flex lg:flex-row lg:overflow-x-auto lg:scroll-smooth lg:gap-2',
            'lg:-mx-4 lg:px-4',
            'lg:pb-4',
          )}
        >
          {renderFoodRanks(foodRanks)}
        </div>
      </div>

      <Button
        asChild
        variant="secondary"
        className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-5"
        data-testid="nutrition-foods-allFoodsButton"
      >
        <Link to={handleAllFoodsRedirection()}>
          All foods
          <ChevronRightIcon height={24} width={24} aria-hidden />
        </Link>
      </Button>

      <NutritionPageServingGuide className="w-full text-center mt-5" />
    </>
  );
};
